import type { UiSlice } from '@/shared/lib/zustand-types';

/**
 * Layout preference. `auto` resolves at runtime via `matchMedia` against
 * the tablet + desktop breakpoints. `mobile`/`tablet`/`desktop` overrides
 * force the layout regardless of viewport — useful for the settings
 * sheet "preview" affordance and for QA on a single device.
 */
export const LayoutPref = {
  Auto: 'auto',
  Mobile: 'mobile',
  Tablet: 'tablet',
  Desktop: 'desktop',
} as const;
export type LayoutPref = (typeof LayoutPref)[keyof typeof LayoutPref];

/** The fully resolved layout (no `auto`) consumed by components + CSS. */
export const ResolvedLayout = {
  Mobile: 'mobile',
  Tablet: 'tablet',
  Desktop: 'desktop',
} as const;
export type ResolvedLayout = (typeof ResolvedLayout)[keyof typeof ResolvedLayout];

export type LayoutSlice = {
  layoutPref: LayoutPref;
  setLayoutPref: (pref: LayoutPref) => void;
};

const STORAGE_KEY = 'infiltre:layout:v1';
const DEFAULT_PREF: LayoutPref = LayoutPref.Auto;

const isPref = (raw: string): raw is LayoutPref =>
  raw === LayoutPref.Auto ||
  raw === LayoutPref.Mobile ||
  raw === LayoutPref.Tablet ||
  raw === LayoutPref.Desktop;

const load = (): LayoutPref => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw && isPref(raw)) return raw;
  } catch {
    // best-effort
  }
  return DEFAULT_PREF;
};

const persist = (pref: LayoutPref): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* quota / private mode — drop */
  }
};

export const createLayoutSlice: UiSlice<LayoutSlice> = (set) => ({
  layoutPref: load(),
  setLayoutPref: (pref) =>
    set((s) => {
      s.layoutPref = pref;
      persist(pref);
    }),
});

/** Tablet breakpoint — 768px through 1279px (sits between mobile and desktop). */
export const TABLET_BREAKPOINT = '(min-width: 768px) and (max-width: 1279px)';
/** Desktop breakpoint — 1280+. Matches the dossier design's wide stage. */
export const DESKTOP_BREAKPOINT = '(min-width: 1280px)';
/** Short-height override — Discord docked / panel / PIP iframes and mobile
 *  landscape all sit under ~600px tall. Force the mobile layout there so
 *  desktop/tablet panels (vertically centered, ~520px tall, no scroll)
 *  don't clip top + bottom. iPad portrait (1024px tall) and the
 *  fullscreen Discord activity (≥720px tall) stay on their resolved
 *  width-based layout. */
export const SHORT_HEIGHT_BREAKPOINT = '(max-height: 600px)';
